import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../components/website/navigation';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { FaRoute, FaChargingStation, FaBatteryFull, FaMapMarkedAlt, FaTruck, FaChartLine, FaSolarPanel, FaLeaf } from 'react-icons/fa';

function FleetOwners() {
    const [heroRef, isHeroVisible] = useScrollAnimation({ threshold: 0.2 });
    const [featuresRef, areFeaturesVisible] = useScrollAnimation({ threshold: 0.2 });
    const [benefitsRef, areBenefitsVisible] = useScrollAnimation({ threshold: 0.2 });
    const [ctaRef, isCtaVisible] = useScrollAnimation({ threshold: 0.2 });

    const features = [
        {
            icon: <FaRoute className="w-8 h-8 text-blue-600" />,
            title: "Intelligent Route Optimization",
            description: "Plan and optimize up to 100 routes with advanced algorithms that consider traffic, distance, and charging requirements."
        },
        {
            icon: <FaChargingStation className="w-8 h-8 text-green-600" />,
            title: "Charging Infrastructure Analysis",
            description: "Identify optimal charging station locations and analyze existing infrastructure to support your electric fleet."
        },
        {
            icon: <FaBatteryFull className="w-8 h-8 text-yellow-600" />,
            title: "Battery & Energy Management",
            description: "Monitor state of charge, optimize charging schedules, and ensure your fleet never runs out of power."
        },
        {
            icon: <FaMapMarkedAlt className="w-8 h-8 text-red-600" />,
            title: "Multi-Depot Planning",
            description: "Manage operations across up to 20 depots with coordinated logistics and resource allocation."
        },
        {
            icon: <FaTruck className="w-8 h-8 text-purple-600" />,
            title: "Fleet Composition Analysis",
            description: "Compare electric truck models and optimize your fleet composition for maximum efficiency and cost savings."
        },
        {
            icon: <FaChartLine className="w-8 h-8 text-indigo-600" />,
            title: "Advanced Analytics",
            description: "Real-time dashboards and detailed reports on fleet performance, costs, and environmental impact."
        },
        {
            icon: <FaSolarPanel className="w-8 h-8 text-orange-600" />,
            title: "Solar Integration",
            description: "Incorporate solar panels and battery storage systems to reduce operational costs and carbon footprint."
        },
        {
            icon: <FaLeaf className="w-8 h-8 text-green-500" />,
            title: "Sustainability Tracking",
            description: "Track emissions reductions, fuel savings, and environmental benefits of your electric fleet transition."
        }
    ];

    const benefits = [
        {
            title: "Reduce Operating Costs",
            description: "Save up to 60% on fuel costs and reduce maintenance expenses with optimized electric fleet operations.",
            stat: "60%",
            statLabel: "Cost Reduction"
        },
        {
            title: "Maximize Fleet Efficiency",
            description: "Intelligent routing and charging optimization ensures maximum uptime and productivity for your fleet.",
            stat: "95%",
            statLabel: "Fleet Uptime"
        },
        {
            title: "Environmental Impact",
            description: "Significantly reduce your carbon footprint and meet sustainability goals with data-driven insights.",
            stat: "Zero",
            statLabel: "Emissions"
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 font-nunito">
            <Navigation />
            
            {/* Hero Section */}
            <section 
                ref={heroRef}
                className={`bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white pt-32 pb-24 px-5 transition-all duration-1000 ease-out transform ${
                    isHeroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        Electrify Your Fleet with
                        <span className="text-blue-300"> Smart Planning</span>
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto">
                        AmpTrans Fleet Planner helps you transition to electric vehicles with intelligent route optimization, 
                        charging infrastructure analysis, and comprehensive fleet management tools.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/subscription"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 hover:shadow-lg"
                        >
                            Start Free Trial
                        </Link>
                        <Link 
                            to="/fleet-planning-pro"
                            className="bg-transparent border-2 border-blue-300 text-blue-300 hover:bg-blue-300 hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200"
                        >
                            View Demo
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section 
                ref={featuresRef}
                className={`py-20 px-5 bg-white transition-all duration-1000 ease-out transform ${
                    areFeaturesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Comprehensive Fleet Planning Solutions
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Everything you need to successfully plan, optimize, and manage your electric fleet operations.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-2"
                            >
                                <div className="mb-4">{feature.icon}</div>
                                <h3 className="text-xl font-semibold text-gray-800 mb-3">{feature.title}</h3>
                                <p className="text-gray-600">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section 
                ref={benefitsRef}
                className={`py-20 px-5 bg-gradient-to-r from-green-50 to-blue-50 transition-all duration-1000 ease-out transform ${
                    areBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Transform Your Fleet Operations
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Join hundreds of fleet operators who have successfully electrified their operations with AmpTrans.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {benefits.map((benefit, index) => (
                            <div 
                                key={index}
                                className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-all duration-300"
                            >
                                <div className="text-4xl font-bold text-blue-600 mb-2">{benefit.stat}</div>
                                <div className="text-sm text-gray-500 mb-4">{benefit.statLabel}</div>
                                <h3 className="text-2xl font-semibold text-gray-800 mb-4">{benefit.title}</h3>
                                <p className="text-gray-600">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use Cases Section */}
            <section className="py-20 px-5 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Perfect for Every Fleet
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Whether you're a small delivery service or a large logistics company, our platform scales to meet your needs.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-blue-50 p-8 rounded-xl">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Drayage Fleets</h3>
                            <p className="text-gray-600 mb-4">
                                Plan routes for short-haul drayage operations with class 8 trucks. Perfect for moving containers from the port to the warehouse.
                            </p>
                            <ul className="text-sm text-gray-600 space-y-2">
                                <li>• Route optimization</li>
                                <li>• Charging station mapping</li>
                                <li>• Depot management</li>
                            </ul>
                        </div>

                        <div className="bg-green-50 p-8 rounded-xl">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Long-Haul Transport</h3>
                            <p className="text-gray-600 mb-4">
                                Plan long-distance routes with strategic charging stops and depot management for heavy-duty electric trucks.
                            </p>
                            <ul className="text-sm text-gray-600 space-y-2">
                                <li>• Multi-depot coordination</li>
                                <li>• Highway charging networks</li>
                                <li>• Load capacity optimization</li>
                            </ul>
                        </div>

                        <div className="bg-purple-50 p-8 rounded-xl">
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Municipal Fleets</h3>
                            <p className="text-gray-600 mb-4">
                                Manage public service vehicles including buses, waste collection, and emergency services with electric alternatives.
                            </p>
                            <ul className="text-sm text-gray-600 space-y-2">
                                <li>• Public charging integration</li>
                                <li>• Service route planning</li>
                                <li>• Environmental reporting</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section 
                ref={ctaRef}
                className={`py-20 px-5 bg-gradient-to-r from-blue-900 to-indigo-900 text-white transition-all duration-1000 ease-out transform ${
                    isCtaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        Ready to Electrify Your Fleet?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100">
                        Start your electrification journey with AmpTrans Fleet Planner. Try it out with our free plan and scale as you grow.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/subscription"
                            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-all duration-200 hover:scale-105"
                        >
                            Start Free Today
                        </Link>
                        <Link 
                            to="/contact"
                            className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200"
                        >
                            Contact Sales
                        </Link>
                    </div>
                    <div className="mt-8 text-sm text-blue-200">
                        Start with a free plan • Cancel anytime • Custom plans available for specific needs
                    </div>
                </div>
            </section>
        </div>
    );
}

export default FleetOwners; 